import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { shouldApplyCustomStyle } from "../../theme/utils";
import { modifySvgColors, getCustomBrandingLogo } from "./utils";
const RESIDE_LOGO_IDENTIFIERS = ["reside-logo", "reside_logo"];
const DEFAULT_LOGO_STYLE = {
    maxWidth: "200px",
    maxHeight: "130px",
    marginBottom: "100px",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    objectPosition: "center",
};
/**
 * BlockImage Component
 *
 * A component for rendering images within form blocks that supports:
 * - Dynamic SVG color modification based on theme (except for Reside logos)
 * - Custom branding for logo images in admission flows
 * - Responsive image styling with customizable alignment
 *
 * Special handling:
 * - Reside logos: Can be customized through branding theme in admission flows
 * - SVG files: Colors are automatically adjusted to match the application theme
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the image
 * @param {string} props.url - Source URL of the image
 * @param {React.CSSProperties} props.style - Custom styles to apply to the image
 * @param {string} props.alt - Alternative text for accessibility
 * @param {string} props.horizontalAlignment - Horizontal alignment of the image
 */
export const BlockImage = ({ id, url, style, alt, horizontalAlignment, }) => {
    const [modifiedSvgUrl, setModifiedSvgUrl] = useState(url);
    const [useWebpFallback, setUseWebpFallback] = useState(false);
    const isResideLogo = RESIDE_LOGO_IDENTIFIERS.some(identifier => url.includes(identifier));
    const useCustomStyle = shouldApplyCustomStyle();
    useEffect(() => {
        let isCancelled = false;
        if (url.toLowerCase().endsWith(".svg") &&
            !RESIDE_LOGO_IDENTIFIERS.some(id => url.includes(id))) {
            modifySvgColors(url).then(svgString => {
                if (!isCancelled) {
                    // Convert the SVG string to a data URL
                    const dataUrl = `data:image/svg+xml;base64,${btoa(svgString)}`;
                    setModifiedSvgUrl(dataUrl);
                }
            });
        }
        else {
            setModifiedSvgUrl(url);
        }
        return () => {
            isCancelled = true;
        };
    }, [url]);
    // Reset webp fallback when URL changes
    useEffect(() => {
        setUseWebpFallback(false);
    }, [url]);
    const { currentImageUrl, fallbackUrl } = useMemo(() => {
        const baseImageUrl = isResideLogo && useCustomStyle
            ? getCustomBrandingLogo(modifiedSvgUrl)
            : modifiedSvgUrl;
        if (!baseImageUrl) {
            return { currentImageUrl: null, fallbackUrl: null };
        }
        const imageExtRegex = /\.(jpg|png)$/i;
        if (imageExtRegex.test(baseImageUrl)) {
            const originalUrl = baseImageUrl;
            const webpUrl = baseImageUrl.replace(imageExtRegex, ".webp");
            // If webp failed, use original; otherwise try webp first
            return {
                currentImageUrl: useWebpFallback ? originalUrl : webpUrl,
                fallbackUrl: originalUrl,
            };
        }
        // Not a jpg/png, use as-is (e.g., SVG, already webp, etc.)
        return { currentImageUrl: baseImageUrl, fallbackUrl: null };
    }, [modifiedSvgUrl, isResideLogo, useCustomStyle, useWebpFallback]);
    const handleImageError = () => {
        // If we have a fallback URL and haven't used it yet, switch to it
        if (fallbackUrl && currentImageUrl !== fallbackUrl) {
            setUseWebpFallback(true);
        }
    };
    const imageStyle = isResideLogo && useCustomStyle ? DEFAULT_LOGO_STYLE : style;
    if (!currentImageUrl) {
        return null;
    }
    return (_jsx("div", { style: { textAlign: horizontalAlignment }, children: _jsx("img", { id: id, alt: alt, src: currentImageUrl, 
            /** skipcq: JS-0760 - False positive: onError handles image load failures (network/404), not mouse/keyboard interaction. Required for webp->original format fallback. */
            onError: handleImageError, style: imageStyle, className: "blockimage-0-62" }) }));
};
